import * as React from 'react';
import {useSelector} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    TaskDeleteInput,
    TaskCreateInput,
    WidgetFragment as Widget,
    TaskUpdateInput,
    CoreEntityTaskFragment as Task,
    TaskMarkUnhelpfulAndDeleteInput,
} from 'nutshell-graphql-types';

import {CollapsibleCardWidget} from '../../../core/card/collapsible-card-widget';
import {useGetSessionUser} from '../../../core/use-get-session-user';

import {TaskForm} from './components/task-form';
import {TaskList} from './components/task-list';
import {useCreateTask} from './graphql/hooks/use-create-task';
import {useDeleteTask} from './graphql/hooks/use-delete-task';
import {useGetDefaultTask} from './graphql/hooks/use-get-default-task';
import {useMarkUnhelpfulAndDeleteTask} from './graphql/hooks/use-mark-unhelpful-and-delete-task';
import {useUpdateTask} from './graphql/hooks/use-update-task';
import {
    getEmptyStateProps,
    getTabOptions,
    getTasksList,
    adjustPositionsAfterDelete,
    getReminderMenuOptions,
    getAddTaskButtonProps,
    getReminderButtonProps,
} from './helpers';

type Props = {
    entityId: string;
    widget: Widget;
    tasks: Task[] | null | undefined;
    isLoadingTasks: boolean;
    refetchQueries: any[];
    showReminderButton?: boolean;
};

export type TaskTab = 'open' | 'completed';

export function TasksWidget(props: Props) {
    const [selectedTab, setSelectedTab] = React.useState<TaskTab>('open');
    const [isAddingTask, setIsAddingTask] = React.useState<boolean>(false);
    const [isReminderMutating, setIsReminderMutating] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null | undefined>(null);
    // We do not want to remove completed tasks from the current tab until tab changes
    const [tasksToKeepInCurrentTab, setTasksToKeepInCurrentTab] = React.useState<
        Map<string, number>
    >(new Map());
    // Recurring tasks are replaced by a successor after completion and
    // disappear from props.tasks as we filter out completed recurring tasks on
    // the backend. We cache them so they remain visible in the current session,
    // matching regular task behavior.
    const [completedRecurringTaskCache, setCompletedRecurringTaskCache] = React.useState<
        Map<string, Task>
    >(new Map());

    const {user} = useGetSessionUser();
    const {defaultTask} = useGetDefaultTask();

    const assignables = useSelector(Assignables.getAllEnabled);

    const {updateTask, isLoading: isUpdatingTask} = useUpdateTask({
        refetchQueries: props.refetchQueries,
    });
    const {deleteTask} = useDeleteTask();
    const {markUnhelpfulAndDeleteTask} = useMarkUnhelpfulAndDeleteTask();
    const {createTask, isLoading: isCreatingTask} = useCreateTask({
        refetchQueries: props.refetchQueries,
    });

    const tasksForDisplay = React.useMemo(() => {
        if (!props.tasks || completedRecurringTaskCache.size === 0) return props.tasks;
        // Re-append any cached recurring tasks that were removed from the server response
        // after their successor was created (backend filters out completed recurring tasks)
        const taskIds = new Set(props.tasks.map((t) => t.id));
        const missing = Array.from(completedRecurringTaskCache.values()).filter(
            (t) => !taskIds.has(t.id)
        );
        return missing.length > 0 ? [...props.tasks, ...missing] : props.tasks;
    }, [props.tasks, completedRecurringTaskCache]);

    const tasksList = React.useMemo(() => {
        return getTasksList(tasksForDisplay, selectedTab, tasksToKeepInCurrentTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tasksForDisplay, selectedTab]);

    const handleTabChange = React.useCallback((newTab: TaskTab) => {
        setTasksToKeepInCurrentTab(new Map());
        setSelectedTab(newTab);
        setIsAddingTask(false);
    }, []);

    const handleAddButtonClick = React.useCallback(() => {
        handleTabChange('open');
        setIsAddingTask(true);
    }, [handleTabChange, setIsAddingTask]);

    const handleCreateTask = React.useCallback(
        (input: TaskCreateInput) => {
            if (!user) {
                return;
            }

            createTask(input)
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                })
                .finally(() => {
                    setIsAddingTask(false);
                });
        },
        [user, createTask]
    );

    const handleDeleteTask = React.useCallback(
        (input: TaskDeleteInput) => {
            return deleteTask(input)
                .then(() => {
                    const deletedTaskIndex = tasksList.findIndex((task) => task.id === input.task);
                    if (deletedTaskIndex !== -1) {
                        setTasksToKeepInCurrentTab((prev) =>
                            adjustPositionsAfterDelete(prev, deletedTaskIndex)
                        );
                    }
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [deleteTask, tasksList]
    );

    const handleMarkUnhelpfulAndDeleteTask = React.useCallback(
        (input: TaskMarkUnhelpfulAndDeleteInput) => {
            return markUnhelpfulAndDeleteTask(input)
                .then(() => {
                    const deletedTaskIndex = tasksList.findIndex((task) => task.id === input.task);
                    if (deletedTaskIndex !== -1) {
                        setTasksToKeepInCurrentTab((prev) =>
                            adjustPositionsAfterDelete(prev, deletedTaskIndex)
                        );
                    }
                })
                .catch((e) => {
                    setError(safelyGetGraphQLErrorMessage(e));
                });
        },
        [markUnhelpfulAndDeleteTask, tasksList]
    );

    const handleUpdateTask = React.useCallback(
        (input: TaskUpdateInput) => {
            // Set state BEFORE mutation to prevent tasks from flickering
            if (input.completion != null) {
                const taskIndex = tasksList.findIndex((task) => task.id === input.task);
                if (taskIndex !== -1) {
                    setTasksToKeepInCurrentTab((prev) => new Map(prev).set(input.task, taskIndex));

                    const task = tasksList[taskIndex];
                    if (task?.isRecurringForUser && input.completion === true) {
                        // Cache optimistically as completed so it stays visible after the
                        // backend replaces it with a new uncompleted successor on refetch
                        setCompletedRecurringTaskCache((prev) =>
                            new Map(prev).set(task.id, {
                                ...task,
                                __typename: 'Task',
                                isCompleted: true,
                            })
                        );
                    }
                }
            }

            return updateTask(input).catch((e) => {
                // Remove task from tasksToKeepInCurrentTab on failure
                if (input.completion != null) {
                    setTasksToKeepInCurrentTab((prev) => {
                        const newMap = new Map(prev);
                        newMap.delete(input.task);

                        return newMap;
                    });
                }
                setError(safelyGetGraphQLErrorMessage(e));
            });
        },
        [updateTask, tasksList]
    );

    const taskMutations = React.useMemo(
        () => ({
            onUpdateTask: handleUpdateTask,
            onDeleteTask: handleDeleteTask,
            onMarkUnhelpfulAndDeleteTask: handleMarkUnhelpfulAndDeleteTask,
        }),
        [handleUpdateTask, handleDeleteTask, handleMarkUnhelpfulAndDeleteTask]
    );

    const recurringTask = React.useMemo(
        () => tasksForDisplay?.find((t) => t.isRecurringForUser) ?? null,
        [tasksForDisplay]
    );

    const handleSetReminder = React.useCallback(
        (days: number, title: string) => {
            if (!recurringTask && !defaultTask) return;

            setIsReminderMutating(true);

            let promise: Promise<any>;

            if (recurringTask) {
                promise = updateTask({task: recurringTask.id, name: title, recurringPeriod: days});
            } else {
                promise = createTask({
                    entity: props.entityId,
                    name: title,
                    dueTime: defaultTask!.dueTime,
                    recurringPeriod: days,
                });
            }

            promise
                .catch((e) => setError(safelyGetGraphQLErrorMessage(e)))
                .finally(() => setIsReminderMutating(false));
        },
        [recurringTask, defaultTask, updateTask, createTask, props.entityId]
    );

    const handleRemoveReminder = React.useCallback(() => {
        if (!recurringTask) return;

        setIsReminderMutating(true);

        deleteTask({task: recurringTask.id})
            .catch((e) => setError(safelyGetGraphQLErrorMessage(e)))
            .finally(() => setIsReminderMutating(false));
    }, [recurringTask, deleteTask]);

    const handleReminderButtonClick = React.useCallback(() => {
        setSelectedTab('open');
    }, []);

    const emptyStateProps = React.useMemo(() => {
        return getEmptyStateProps(tasksList, selectedTab, isAddingTask);
    }, [selectedTab, tasksList, isAddingTask]);

    const tabProps = React.useMemo(() => {
        return getTabOptions(tasksForDisplay, selectedTab, handleTabChange);
    }, [tasksForDisplay, selectedTab, handleTabChange]);

    const reminderMenuOptions = React.useMemo(
        () => getReminderMenuOptions(recurringTask, handleSetReminder, handleRemoveReminder),
        [recurringTask, handleSetReminder, handleRemoveReminder]
    );

    const upcomingTasksCount = tasksForDisplay
        ? tasksForDisplay.filter((task) => task.isCompleted === false).length
        : 0;

    const addTaskButtonProps = getAddTaskButtonProps(handleAddButtonClick, isAddingTask);
    const reminderButtonProps = getReminderButtonProps(
        reminderMenuOptions,
        isReminderMutating,
        handleReminderButtonClick
    );

    return (
        <CollapsibleCardWidget
            widget={props.widget}
            addButtonProps={addTaskButtonProps}
            secondaryAddButtonProps={props.showReminderButton ? reminderButtonProps : undefined}
            emptyStateProps={emptyStateProps}
            isLoading={props.isLoadingTasks}
            errorProps={{
                error,
                setError,
            }}
            hasBorder={true}
            tabProps={{tabs: tabProps}}
            count={upcomingTasksCount}
            setIsAdding={(isAdding) => {
                setIsAddingTask(isAdding);
            }}
        >
            <>
                {isAddingTask && user && defaultTask && (
                    <div className='pad-16 pb-8'>
                        <TaskForm
                            // @ts-expect-error
                            assignables={assignables}
                            defaultTask={defaultTask}
                            entityId={props.entityId}
                            onCancel={() => setIsAddingTask(false)}
                            onCreate={handleCreateTask}
                            isSaving={isCreatingTask}
                        />
                    </div>
                )}
                <TaskList
                    tasks={tasksList}
                    taskMutations={taskMutations}
                    setError={setError}
                    isUpdatingTask={isUpdatingTask}
                />
            </>
        </CollapsibleCardWidget>
    );
}
